import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, BookHeart, Plus, X, Trash2, Lock, Pencil, Loader2, Users } from 'lucide-react';
import { ShareReflectionComposer } from '../components/programs/ShareReflectionComposer';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MOODS = [
  { id: 'calm', emoji: '🧘', label: 'Calm', dot: 'bg-sky-400' },
  { id: 'grateful', emoji: '🙏', label: 'Grateful', dot: 'bg-amber-400' },
  { id: 'happy', emoji: '😊', label: 'Happy', dot: 'bg-yellow-400' },
  { id: 'reflective', emoji: '💭', label: 'Reflective', dot: 'bg-violet-400' },
  { id: 'anxious', emoji: '😟', label: 'Anxious', dot: 'bg-orange-400' },
  { id: 'sad', emoji: '🌧️', label: 'Sad', dot: 'bg-blue-500' },
  { id: 'tired', emoji: '😴', label: 'Tired', dot: 'bg-stone-400' },
  { id: 'hopeful', emoji: '🌱', label: 'Hopeful', dot: 'bg-emerald-400' },
];

const moodEmoji = (id) => MOODS.find((m) => m.id === id)?.emoji || '';
const moodLabel = (id) => MOODS.find((m) => m.id === id)?.label || '';
const moodDot = (id) => MOODS.find((m) => m.id === id)?.dot || 'bg-stone-300';

const formatDate = (iso) => {
  const d = new Date(iso);
  const today = new Date();
  const isToday = d.toDateString() === today.toDateString();
  const yest = new Date();
  yest.setDate(today.getDate() - 1);
  const isYesterday = d.toDateString() === yest.toDateString();
  const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  if (isToday) return `Today · ${time}`;
  if (isYesterday) return `Yesterday · ${time}`;
  return d.toLocaleDateString('en', { month: 'short', day: 'numeric', year: 'numeric' }) + ` · ${time}`;
};

const cardCls = 'bg-[#FFFCF3] border border-stone-200';

const JournalPage = () => {
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const [entries, setEntries] = useState([]);
  const [shareEntry, setShareEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activeDotId, setActiveDotId] = useState(null);

  // Memoize the most-recent slice used by the mood timeline so it doesn't
  // re-allocate on every render (e.g. when toggling activeDotId).
  const recentEntries = useMemo(
    () => entries.slice(0, 30).slice().reverse(),
    [entries]
  );
  const activeEntry = useMemo(
    () => recentEntries.find((e) => e.id === activeDotId) || null,
    [recentEntries, activeDotId]
  );

  const token = () => localStorage.getItem('rbr_token');

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/journal`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      if (res.ok) setEntries(await res.json());
    } catch {
      toast.error('Could not load journal');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/auth');
      return;
    }
    load();
  }, [authLoading, user]);

  const openNew = () => setEditor({ title: '', content: '', mood: '' });
  const openEdit = (e) => setEditor({ id: e.id, title: e.title || '', content: e.content || '', mood: e.mood || '' });
  const closeEditor = () => setEditor(null);

  const save = async () => {
    if (!editor.content.trim()) {
      toast.error('Write something first');
      return;
    }
    setSaving(true);
    try {
      const isEdit = !!editor.id;
      const url = isEdit ? `${API_URL}/api/journal/${editor.id}` : `${API_URL}/api/journal`;
      const method = isEdit ? 'PATCH' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: editor.title.trim(),
          content: editor.content.trim(),
          mood: editor.mood || null,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Could not save');
        return;
      }
      toast.success(isEdit ? 'Entry updated' : 'Entry saved');
      closeEditor();
      await load();
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this entry? This cannot be undone.')) return;
    try {
      const res = await fetch(`${API_URL}/api/journal/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token()}` },
      });
      if (res.ok) {
        toast.success('Entry deleted');
        setEntries((prev) => prev.filter((e) => e.id !== id));
      }
    } catch {
      toast.error('Could not delete');
    }
  };

  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-5 h-5 animate-spin text-[#D6AD60]" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in pb-6" data-testid="journal-page">
      {/* Header */}
      <div className="px-5 pt-4 flex items-center justify-between mb-4">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-stone-500 hover:text-[#D6AD60] transition-colors duration-300"
          data-testid="journal-back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm">Back</span>
        </button>
        <button
          onClick={openNew}
          className="flex items-center gap-1.5 px-3 py-2 btn-gold text-xs font-medium rounded-full transition-colors"
          data-testid="journal-new-btn"
        >
          <Plus className="w-3.5 h-3.5" /> New entry
        </button>
      </div>

      {/* Title card */}
      <div className="px-5 mb-5">
        <div className={`${cardCls} rounded-3xl p-5 shadow-md`}>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#D6AD60]/30 to-[#D6AD60]/20 flex items-center justify-center">
              <BookHeart className="w-5 h-5 text-[#AA8347]" />
            </div>
            <div className="flex-1 min-w-0">
              <h1
                className="text-lg font-semibold text-stone-800"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                My Journal
              </h1>
              <p className="text-[11px] text-stone-500 flex items-center gap-1">
                <Lock className="w-3 h-3" /> Private — only you can see this
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Mood timeline */}
      {entries.length >= 2 && (
        <div className="px-5 mb-5">
          <div className={`${cardCls} rounded-3xl p-4 shadow-md`} data-testid="mood-timeline">
            <div className="flex items-center justify-between mb-3">
              <p className="text-[11px] font-semibold text-[#D6AD60] uppercase tracking-wider">
                Mood timeline
              </p>
              <p className="text-[10px] text-stone-500">
                {recentEntries.length} {recentEntries.length === 1 ? 'entry' : 'entries'}
              </p>
            </div>

            <div
              className="flex items-center gap-1.5 overflow-x-auto pb-1 -mx-1 px-1 scroll-smooth"
              onMouseLeave={() => setActiveDotId(null)}
            >
              {recentEntries.map((e) => {
                const isActive = activeDotId === e.id;
                return (
                  <button
                    key={e.id}
                    onClick={() => {
                      if (isActive) {
                        const el = document.querySelector(`[data-testid="journal-entry-${e.id}"]`);
                        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        setActiveDotId(null);
                      } else {
                        setActiveDotId(e.id);
                      }
                      }}
                      onMouseEnter={() => setActiveDotId(e.id)}
                      className={`relative flex-shrink-0 transition-transform ${
                        isActive ? 'scale-150' : 'hover:scale-125'
                      }`}
                      data-testid={`mood-dot-${e.id}`}
                      aria-label={`Entry from ${new Date(e.createdAt).toLocaleDateString()} — ${moodLabel(e.mood) || 'no mood'}`}
                    >
                      <span
                        className={`block w-3 h-3 rounded-full ring-2 ring-[#1a1814] ${moodDot(e.mood)} ${
                          isActive ? 'ring-[#D6AD60]' : ''
                        }`}
                      />
                    </button>
                  );
                })}
              </div>

              {activeEntry && (
                <div
                  className="mt-3 bg-[#E8DDCB] rounded-xl p-3 border-l-4 border-[#D6AD60] animate-fade-in"
                  data-testid="mood-timeline-preview"
                >
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <p className="text-[10px] font-semibold text-[#D6AD60] uppercase tracking-wider">
                      {formatDate(activeEntry.createdAt)}
                    </p>
                    {activeEntry.mood && (
                      <span className="text-[11px] text-stone-800 font-medium flex items-center gap-1">
                        {moodEmoji(activeEntry.mood)} {moodLabel(activeEntry.mood)}
                      </span>
                    )}
                  </div>
                  {activeEntry.title && (
                    <p className="text-[12px] font-semibold text-stone-800 mb-0.5 line-clamp-1">
                      {activeEntry.title}
                    </p>
                  )}
                  <p className="text-[12px] text-stone-500 leading-snug line-clamp-2">
                    {activeEntry.content}
                  </p>
                  <button
                    onClick={() => {
                      const el = document.querySelector(`[data-testid="journal-entry-${activeEntry.id}"]`);
                      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      setActiveDotId(null);
                    }}
                    className="text-[10px] text-[#D6AD60] hover:text-[#AA8347] font-medium mt-1.5"
                    data-testid="mood-timeline-jump"
                  >
                    Jump to entry →
                  </button>
                </div>
              )}

              {!activeEntry && (
                <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1">
                  {MOODS.filter((m) => recentEntries.some((e) => e.mood === m.id)).map((m) => (
                    <span key={m.id} className="flex items-center gap-1 text-[10px] text-stone-500">
                      <span className={`w-2 h-2 rounded-full ${m.dot}`} />
                      {m.label}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
      )}

      {/* List */}
      <div className="px-5">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-5 h-5 animate-spin text-[#D6AD60]" />
          </div>
        ) : entries.length === 0 ? (
          <div className={`${cardCls} rounded-3xl p-8 shadow-md text-center`}>
            <div className="w-12 h-12 mx-auto rounded-2xl bg-[#E8DDCB] flex items-center justify-center mb-3">
              <BookHeart className="w-5 h-5 text-stone-500" />
            </div>
            <p className="text-sm text-stone-800 mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
              Your journal is waiting whenever you&rsquo;re ready.
            </p>
            <p className="text-[11px] text-stone-500 mb-4">
              Capture a thought, a feeling, or a moment of gratitude.
            </p>
            <button
              onClick={openNew}
              className="px-4 py-2 btn-gold text-xs font-medium rounded-full"
              data-testid="journal-empty-new-btn"
            >
              Write your first entry
            </button>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {entries.map((e) => (
              <article
                key={e.id}
                className={`${cardCls} rounded-2xl p-4 shadow-md hover:border-[#D6AD60]/30 hover:shadow-lg transition-all`}
                data-testid={`journal-entry-${e.id}`}
              >
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="flex-1 min-w-0">
                    {e.title && (
                      <h3
                        className="text-sm font-semibold text-stone-800 truncate"
                        style={{ fontFamily: "'Playfair Display', serif" }}
                      >
                        {e.title}
                      </h3>
                    )}
                    <p className="text-[10px] text-stone-500 mt-0.5">
                      {formatDate(e.createdAt)}
                      {e.mood && (
                        <span className="ml-2">
                          {moodEmoji(e.mood)} {moodLabel(e.mood)}
                        </span>
                      )}
                    </p>
                  </div>
                  <div className="flex gap-1">
                    <button
                      onClick={() => openEdit(e)}
                      className="w-7 h-7 rounded-lg hover:bg-[#E8DDCB] flex items-center justify-center text-stone-500 hover:text-[#D6AD60]"
                      data-testid={`journal-edit-${e.id}`}
                      aria-label="Edit"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => remove(e.id)}
                      className="w-7 h-7 rounded-lg hover:bg-red-500/10 flex items-center justify-center text-stone-500 hover:text-red-400"
                      data-testid={`journal-delete-${e.id}`}
                      aria-label="Delete"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
                <p className="text-[13px] text-stone-700 leading-relaxed whitespace-pre-wrap break-words">
                  {e.content}
                </p>
                {e.programId && (
                  <button type="button" onClick={() => setShareEntry(e)} className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-semibold text-[#1e3a5f] hover:text-[#a88a4c]" data-testid={`journal-share-${e.id}`}>
                    <Users className="w-3.5 h-3.5 text-[#D6AD60]" /> Share this reflection
                  </button>
                )}
              </article>
            ))}
          </div>
        )}
      </div>

      <ShareReflectionComposer open={!!shareEntry} onClose={() => setShareEntry(null)} text={shareEntry?.content} circleId={shareEntry?.circleId}
        circleName={shareEntry?.circleName} prompt={shareEntry?.circlePrompt} programSlug={shareEntry?.programSlug} programDayId={shareEntry?.programDayId} day={shareEntry?.dayNumber} />

      {/* Editor modal */}
      {editor && createPortal(
        <div
          className="fixed inset-0 z-[80] flex items-end justify-center bg-black/60 backdrop-blur-sm animate-fade-in"
          onClick={closeEditor}
          data-testid="journal-editor-overlay"
        >
          <div
            className="bg-[#FFFCF3] border-t border-x border-[#D6AD60]/25 w-full max-w-[480px] rounded-t-3xl shadow-2xl animate-slide-up max-h-[92vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
            data-testid="journal-editor"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-stone-200">
              <h3 className="text-base font-semibold text-stone-800" style={{ fontFamily: "'Playfair Display', serif" }}>
                {editor.id ? 'Edit entry' : 'New entry'}
              </h3>
              <button
                onClick={closeEditor}
                className="w-8 h-8 rounded-full bg-[#E8DDCB] hover:bg-[#DBC9A3] flex items-center justify-center"
                data-testid="journal-editor-close"
                aria-label="Close"
              >
                <X className="w-4 h-4 text-[#D6AD60]" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
              <input
                type="text"
                value={editor.title}
                onChange={(e) => setEditor({ ...editor, title: e.target.value })}
                placeholder="Title (optional)"
                maxLength={200}
                className="w-full px-4 py-3 bg-[#E8DDCB] border border-stone-200 rounded-xl text-sm text-stone-800 placeholder:text-stone-500 focus:outline-none focus:ring-2 focus:ring-[#D6AD60]/40"
                data-testid="journal-title-input"
              />

              <div>
                <p className="text-[11px] font-semibold text-[#D6AD60] uppercase tracking-wider mb-2">How are you feeling?</p>
                <div className="flex flex-wrap gap-2">
                  {MOODS.map((m) => (
                    <button
                      key={m.id}
                      onClick={() => setEditor({ ...editor, mood: editor.mood === m.id ? '' : m.id })}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs transition-all ${
                        editor.mood === m.id
                          ? 'bg-gradient-to-br from-[#D6AD60] to-[#D6AD60] text-[#1a1814] shadow-md font-semibold'
                          : 'bg-[#E8DDCB] text-stone-800 hover:bg-[#DBC9A3]'
                      }`}
                      data-testid={`journal-mood-${m.id}`}
                    >
                      <span>{m.emoji}</span> {m.label}
                    </button>
                  ))}
                </div>
              </div>

              <textarea
                value={editor.content}
                onChange={(e) => setEditor({ ...editor, content: e.target.value })}
                placeholder="What's on your mind?"
                rows={10}
                maxLength={10000}
                className="w-full px-4 py-3 bg-[#E8DDCB] border border-stone-200 rounded-xl text-sm text-stone-800 placeholder:text-stone-500 focus:outline-none focus:ring-2 focus:ring-[#D6AD60]/40 resize-none leading-relaxed"
                data-testid="journal-content-input"
                autoFocus
              />
              <p className="text-[10px] text-stone-500 text-right">{editor.content.length}/10000</p>
            </div>

            <div className="px-5 py-3 border-t border-stone-200 flex gap-2">
              <button
                onClick={closeEditor}
                disabled={saving}
                className="flex-1 py-2.5 rounded-xl bg-[#E8DDCB] hover:bg-[#DBC9A3] text-stone-800 text-sm font-medium transition-colors"
                data-testid="journal-cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={save}
                disabled={saving || !editor.content.trim()}
                className="flex-1 py-2.5 rounded-xl btn-gold text-sm font-semibold transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="journal-save-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                {editor.id ? 'Save changes' : 'Save entry'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default JournalPage;
